import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Heart, Plus, MessageSquare, BookOpen, AlertCircle, User, X, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NOTE_ICONS = { meeting: MessageSquare, academic: BookOpen, personal: Heart, alert: AlertCircle };
const NOTE_STYLES = {
  meeting: { color: "#0A7AFF", bg: "#EFF6FF" },
  academic: { color: "#22C55E", bg: "#F0FDF4" },
  personal: { color: "#8B5CF6", bg: "#F5F3FF" },
  alert: { color: "#FF3636", bg: "#FFF1F2" },
};

export default function MentorMentee() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ note: "", note_type: "meeting" });
  const [assignForm, setAssignForm] = useState({ mentor_name: "", mentor_email: "", student_name: "", student_email: "" });
  const [addingNote, setAddingNote] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => { fetchAssignments(); }, []);

  const fetchAssignments = async () => {
    try {
      const { data } = await axios.get(`${API}/mentor/assignments`, { withCredentials: true });
      setAssignments(data);
      if (data.length > 0) setSelected(prev => prev ? (data.find(a => a._id === prev._id) || data[0]) : data[0]);
    } catch {} finally { setLoading(false); }
  };

  const handleAddNote = async (e) => {
    e.preventDefault(); setAddingNote(true);
    try {
      await axios.post(`${API}/mentor/notes`, { ...noteForm, assignment_id: selected._id }, { withCredentials: true });
      setShowNoteModal(false);
      setNoteForm({ note: "", note_type: "meeting" });
      fetchAssignments();
    } catch {} finally { setAddingNote(false); }
  };

  const handleAssign = async (e) => {
    e.preventDefault(); setAssigning(true);
    try {
      await axios.post(`${API}/mentor/assign`, assignForm, { withCredentials: true });
      setShowAssignModal(false);
      setAssignForm({ mentor_name: "", mentor_email: "", student_name: "", student_email: "" });
      fetchAssignments();
    } catch {} finally { setAssigning(false); }
  };

  const isStudent = user?.role === "student";
  const selectedFull = selected ? assignments.find(a => a._id === selected._id) || selected : null;

  return (
    <div className="space-y-6" data-testid="mentor-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Academic</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Mentor-Mentee</h2>
        </div>
        <button onClick={() => setShowAssignModal(true)} data-testid="assign-mentor-btn"
          className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Assign Mentor
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Assignments", value: assignments.length, color: "#8B5CF6" },
          { label: "Active", value: assignments.filter(a => a.status === "active").length, color: "#22C55E" },
          { label: "With Notes", value: assignments.filter(a => a.notes?.length > 0).length, color: "#0A7AFF" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Split panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5" style={{ minHeight: "480px" }}>
        {/* Left: List */}
        <div className="bg-white border border-[#E2DDD4]">
          <div className="px-4 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]">
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{isStudent ? "My Mentor" : "Mentees"}</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-4 h-4 animate-spin text-[#BDBAA8]" /></div>
          ) : assignments.length === 0 ? (
            <div className="p-8 text-center">
              <Heart className="w-8 h-8 text-[#D8D4CB] mx-auto mb-2" strokeWidth={1} />
              <p className="font-['DM_Sans'] text-sm text-[#BDBAA8]">No assignments yet</p>
            </div>
          ) : (
            <div>
              {assignments.map(a => {
                const displayName = isStudent ? a.mentor_name : a.student_name;
                const displayEmail = isStudent ? a.mentor_email : a.student_email;
                const isActive = selectedFull?._id === a._id;
                return (
                  <button key={a._id} onClick={() => setSelected(a)}
                    className={`w-full flex items-center gap-3 p-4 text-left hover:bg-[#FAFAF7] transition-colors border-b border-[#F0ECE4] last:border-0 ${isActive ? "bg-[#FAFAF7] border-l-2 border-l-[#8B5CF6] pl-[14px]" : ""}`}>
                    <div className="w-8 h-8 bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 flex items-center justify-center flex-shrink-0">
                      <span className="font-['Syne'] font-bold text-sm text-[#8B5CF6]">{displayName?.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Syne'] font-bold text-xs text-[#0C0C10] truncate">{displayName}</p>
                      <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8] truncate">{displayEmail}</p>
                      <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-wider text-[#22C55E] mt-0.5">{a.notes?.length || 0} notes</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Notes */}
        <div className="bg-white border border-[#E2DDD4]">
          {selectedFull ? (
            <>
              <div className="px-6 py-4 border-b border-[#E2DDD4] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 flex items-center justify-center">
                    <span className="font-['Syne'] font-bold text-base text-[#8B5CF6]">
                      {(isStudent ? selectedFull.mentor_name : selectedFull.student_name)?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-['Syne'] font-bold text-sm text-[#0C0C10]">{isStudent ? selectedFull.mentor_name : selectedFull.student_name}</p>
                    <p className="font-['DM_Sans'] text-xs text-[#7A7468]">{isStudent ? `My Mentor — ${selectedFull.mentor_email}` : `Mentee — Assigned ${new Date(selectedFull.assigned_at).toLocaleDateString("en-IN")}`}</p>
                  </div>
                </div>
                <button onClick={() => setShowNoteModal(true)} data-testid="add-note-btn"
                  className="border border-[#D8D4CB] text-[#0C0C10] font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-4 py-2 hover:bg-[#0C0C10] hover:text-[#EAFF00] hover:border-[#0C0C10] transition-all flex items-center gap-1.5">
                  <Plus className="w-3 h-3" /> Note
                </button>
              </div>

              <div className="p-6 overflow-y-auto" style={{ maxHeight: "400px" }}>
                {!selectedFull.notes?.length ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-8 h-8 text-[#D8D4CB] mx-auto mb-2" strokeWidth={1} />
                    <p className="font-['DM_Sans'] text-sm text-[#BDBAA8]">No notes yet. Add your first note.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedFull.notes.map((note, i) => {
                      const Icon = NOTE_ICONS[note.note_type] || MessageSquare;
                      const s = NOTE_STYLES[note.note_type] || NOTE_STYLES.meeting;
                      return (
                        <div key={i} className="flex items-start gap-3 pb-4 border-b border-[#F0ECE4] last:border-0">
                          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: s.bg, border: `1px solid ${s.color}25` }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider px-2 py-0.5"
                                style={{ color: s.color, background: s.bg }}>{note.note_type}</span>
                              <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{note.added_by}</span>
                              <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">·</span>
                              <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{new Date(note.added_at).toLocaleDateString("en-IN")}</span>
                            </div>
                            <p className="font-['DM_Sans'] text-sm text-[#0C0C10] leading-relaxed">{note.note}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-20">
              <Heart className="w-10 h-10 text-[#D8D4CB] mb-3" strokeWidth={1} />
              <p className="font-['DM_Sans'] text-sm text-[#BDBAA8]">Select a mentee to view notes</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      <AnimatePresence>
        {showNoteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={e => e.target === e.currentTarget && setShowNoteModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-5">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Add Note</h3>
                <button onClick={() => setShowNoteModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <form onSubmit={handleAddNote} className="space-y-5">
                <div>
                  <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Type</label>
                  <div className="grid grid-cols-4 gap-2">
                    {["meeting", "academic", "personal", "alert"].map(t => (
                      <button key={t} type="button" onClick={() => setNoteForm(p => ({ ...p, note_type: t }))}
                        className={`py-2 font-['Syne'] font-bold text-[9px] uppercase tracking-wider border transition-all ${noteForm.note_type === t ? "bg-[#0C0C10] text-[#EAFF00] border-[#0C0C10]" : "border-[#D8D4CB] text-[#7A7468] hover:border-[#0C0C10]"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Note</label>
                  <textarea value={noteForm.note} onChange={e => setNoteForm(p => ({ ...p, note: e.target.value }))} required rows={4}
                    placeholder="Write meeting notes, academic observations, or alerts..."
                    className="w-full bg-transparent border border-[#D8D4CB] p-3 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] resize-none transition-colors" />
                </div>
                <button type="submit" disabled={addingNote} className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50">
                  {addingNote ? "Saving..." : "Add Note"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Modal */}
      <AnimatePresence>
        {showAssignModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={e => e.target === e.currentTarget && setShowAssignModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Assign Mentor</h3>
                <button onClick={() => setShowAssignModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <form onSubmit={handleAssign} className="space-y-4">
                {[["mentor_name", "Mentor Name"], ["mentor_email", "Mentor Email"], ["student_name", "Student Name"], ["student_email", "Student Email"]].map(([f, l]) => (
                  <div key={f}>
                    <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                    <input type={f.includes("email") ? "email" : "text"} value={assignForm[f]} onChange={e => setAssignForm(p => ({ ...p, [f]: e.target.value }))} required
                      placeholder={f === "mentor_email" ? "priya.nair@inst.edu" : f === "student_email" ? "student@acadence.edu" : ""}
                      className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                  </div>
                ))}
                <button type="submit" disabled={assigning} className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 mt-2">
                  {assigning ? "Assigning..." : "Assign Mentor"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
